import { PasswordChecklist, meetsPolicy } from '@/components/PasswordChecklist';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/stores/auth';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Cambio obligatorio en el primer ingreso. Hasta que se complete, el backend
 * responde 403 a cualquier otra ruta, asi que esta pantalla es la unica salida.
 */
export function ChangePasswordPage() {
  const changePassword = useAuth((state) => state.changePassword);
  const user = useAuth((state) => state.user);
  const logout = useAuth((state) => state.logout);
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const mismatch = confirmation.length > 0 && confirmation !== newPassword;
  const canSubmit =
    currentPassword.length > 0 && meetsPolicy(newPassword) && !mismatch && confirmation.length > 0;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await changePassword(currentPassword, newPassword);
      navigate('/tablero', { replace: true });
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError(0, 'No se pudo conectar con el servidor'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-canvas px-6 py-12">
      <div className="w-full max-w-[420px]">
        <div className="mb-7 flex items-center gap-2.5">
          <div
            aria-hidden="true"
            className="flex size-[30px] items-center justify-center rounded-[9px] font-display text-[15px] font-bold text-white"
            style={{ background: 'linear-gradient(135deg,#14b8a6,#22d3ee)' }}
          >
            S
          </div>
          <span className="font-display text-base font-bold tracking-[-.01em]">SynQ</span>
        </div>

        <div className="rounded-panel border border-line bg-surface p-6">
          <h1 className="font-display text-[20px] font-semibold tracking-[-.015em]">
            {user?.passwordExpired ? 'Renueva tu contrasena' : 'Crea tu contrasena'}
          </h1>
          <p className="mt-1.5 text-[13.5px] leading-relaxed text-subtle text-pretty">
            {user?.passwordExpired
              ? `${user.fullName.split(' ')[0]}, tu contrasena cumplio su periodo de vigencia. Elige una nueva para seguir.`
              : `${user?.fullName?.split(' ')[0] ?? ''}, la clave que te dieron es temporal. Elige una propia para continuar.`}
          </p>

          <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4" noValidate>
            <div>
              <label htmlFor="current" className="mb-1.5 block text-[12.5px] font-semibold">
                {user?.passwordExpired ? 'Contrasena actual' : 'Contrasena temporal'}
              </label>
              <input
                id="current"
                type="password"
                autoComplete="current-password"
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full rounded-control border border-line-strong px-3 py-2.5 text-[13.5px] outline-none transition-colors focus:border-brand"
              />
            </div>

            <div>
              <label htmlFor="next" className="mb-1.5 block text-[12.5px] font-semibold">
                Nueva contrasena
              </label>
              <input
                id="next"
                type="password"
                autoComplete="new-password"
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full rounded-control border border-line-strong px-3 py-2.5 text-[13.5px] outline-none transition-colors focus:border-brand"
              />
              <div className="mt-2.5 rounded-control bg-sunken p-3">
                <PasswordChecklist password={newPassword} />
              </div>
            </div>

            <div>
              <label htmlFor="confirm" className="mb-1.5 block text-[12.5px] font-semibold">
                Confirma la nueva contrasena
              </label>
              <input
                id="confirm"
                type="password"
                autoComplete="new-password"
                required
                aria-invalid={mismatch}
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                className={`w-full rounded-control border px-3 py-2.5 text-[13.5px] outline-none transition-colors ${
                  mismatch ? 'border-danger' : 'border-line-strong focus:border-brand'
                }`}
              />
              {mismatch && (
                <p className="mt-1.5 text-[11.5px] text-danger">Las contrasenas no coinciden</p>
              )}
            </div>

            {error && (
              <div
                role="alert"
                className="rounded-control border border-danger-line bg-danger-soft px-3 py-2 text-[13px] text-danger"
              >
                <p>{error.message}</p>
                {error.errors && error.errors.length > 0 && (
                  <ul className="mt-1 list-inside list-disc text-[11.5px]">
                    {error.errors.map((detail) => (
                      <li key={detail}>{detail}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={!canSubmit || submitting}
              className="mt-1 w-full rounded-control bg-brand px-3 py-2.5 text-[13.5px] font-semibold text-brand-on transition-colors hover:bg-brand-strong disabled:opacity-50"
            >
              {submitting ? 'Guardando…' : 'Guardar y entrar'}
            </button>
          </form>
        </div>

        <button
          type="button"
          onClick={() => void logout().then(() => navigate('/login', { replace: true }))}
          className="mt-4 w-full text-center text-[12px] text-subtle transition-colors hover:text-ink"
        >
          Cerrar sesion
        </button>
      </div>
    </main>
  );
}
